import React, {useEffect, useRef} from 'react'
import {
  formatAustralianMobile,
  formatInvitedAgo,
  getInitials,
  isDeclinedInviteStatus,
  isInviteExpired,
  toStatusLabel,
} from '../renterHomeUtils'
import {HouseholdPerson} from './HouseholdTab'

type HouseholdPersonRowProps = {
  person: HouseholdPerson
  isPrimary?: boolean
  isCurrentUser?: boolean
  isMenuOpen: boolean
  onMenuToggle: () => void
  onMenuClose: () => void
  onResendInvite?: () => void
  onRemoveInvite?: () => void
  removeLabel?: string
  isResending?: boolean
  resendRetrySeconds?: number
  isRemoving?: boolean
}

const formatPersonContact = (person: HouseholdPerson): string => {
  const mobile = formatAustralianMobile(person.mobileNumber)
  const values = [mobile, person.email].filter(Boolean)
  return values.length > 0 ? values.join(' - ') : 'Contact details pending'
}

const formatPersonMobile = (person: HouseholdPerson): string => {
  const mobile = formatAustralianMobile(person.mobileNumber)
  const values = [mobile].filter(Boolean)
  return values.length > 0 ? values.join(' - ') : 'Contact details pending'
}

export const HouseholdPersonRow: React.FC<HouseholdPersonRowProps> = ({
  person,
  isPrimary,
  isCurrentUser,
  isMenuOpen,
  onMenuToggle,
  onMenuClose,
  onResendInvite,
  onRemoveInvite,
  removeLabel = 'Remove',
  isResending,
  resendRetrySeconds = 0,
  isRemoving,
}) => {
  const menuRef = useRef<HTMLDivElement>(null)
  const normalizedStatus = person.status?.trim().toLowerCase()
  const isDeclined = isDeclinedInviteStatus(normalizedStatus, person.declinedAtUtc, person.declineReason)
  const isExpired = normalizedStatus === 'expired'
    || (!isDeclined && person.isPendingInvite === true && isInviteExpired(person.inviteExpiresAtUtc))
  const isMobileVerified = person.isVerified === true
  const isAcceptedInvite =
    !isPrimary &&
    (normalizedStatus === 'accepted' ||
      normalizedStatus === 'verified' ||
      (Boolean(person.inviteId) && isMobileVerified))
  const isCardComplete = person.isCardComplete === true
  const isCardIncomplete = person.isCardComplete === false
  const missingRequiredFields = person.missingRequiredFields ?? []
  const hasMissingFields = !isDeclined && missingRequiredFields.length > 0
  const isPositiveStatus = isAcceptedInvite || (isMobileVerified && !isCardIncomplete && !person.isPendingInvite)
  const statusLabel = isDeclined
    ? "This Isn't Me"
    : isExpired
    ? 'Expired'
    : person.isPendingInvite
    ? toStatusLabel(person.status || 'pending')
    : isAcceptedInvite
    ? 'Accepted'
    : !isMobileVerified
    ? 'Mobile unverified'
    : isCardIncomplete
    ? 'Card incomplete'
    : isCardComplete
    ? 'Complete'
    : 'Verified'

  const canResendInvite =
    person.canResend ??
    (!isDeclined && !person.isVerified && (isExpired || isInviteExpired(person.otpExpiresAt)))

  useEffect(() => {
    if (!isMenuOpen) return

    const handleOutsideClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        onMenuClose()
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [isMenuOpen, onMenuClose])

  return (
    <>
      <div className={`renter-household-card ${hasMissingFields ? 'has-missing-fields' : ''} ${isDeclined ? 'is-declined' : ''}`}>
        <div className={`inner ${isPrimary ? 'primary' : ''}`}></div>
        <div className={`renter-household-avatar ${isPrimary ? 'primary' : ''}`}>
          {getInitials(person.name)}
        </div>
        <div className='renter-household-info'>
          <div className='name-info'>
            <h3 className='name'>{person.name}</h3>
            {isCurrentUser && <span className='you'>YOU</span>}
          </div>
          <div className='phone'>{formatPersonMobile(person)}</div>
          <div className='appplicant-type'>
            {person.role || 'Co-applicant'}
            {person.relationship?.trim() ? ` - ${person.relationship.trim()}` : ''}
            {isDeclined 
                ? ' - Invitation declined'
                : ! person.isVerified
                ? ` - ${formatInvitedAgo(person.invitedAtUtc)}`
                : ''}
          </div>
          {isDeclined && (
              <div className='renter-household-declined-message'>
                They selected "This isn't me". The contact details may be wrong.
              </div>
          )}

          {hasMissingFields && (
            <div className='renter-household-missing'>
              Missing: {missingRequiredFields.map(toStatusLabel).join(', ')}
            </div>
          )}
        </div>
        <span className={`pill  status ${isDeclined ? 'pill-declined' : isExpired ? 'pill-expired' : isPositiveStatus ? 'pill-verified' : 'pill-pending'}`}>
          {isDeclined ? (
              <svg
                width='11'
                height='11'
                viewBox='0 0 20 20'
                fill='none'
                stroke='#B42318'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M10 2.518 14H218-14z'></path>
                <path d='M10 8v3.5M10 14v0'></path>
              </svg>
          ) : isPositiveStatus ? (
            <svg
              width='11'
              height='11'
              viewBox='0 0 20 20'
              fill='none'
              stroke='#0A7C5A'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M3.5 10.5l4 4 9-9'></path>
            </svg>
          ) : (
            <svg
              width='11'
              height='11'
              viewBox='0 0 20 20'
              fill='none'
              stroke={isExpired ? '#B42318' : '#B4530A'}
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <circle cx='10' cy='10' r='7'></circle>
              <path d='M10 6v4l2.5 2'></path>
            </svg>
          )}{' '}
          {statusLabel}
        </span>
        {onRemoveInvite && (
          <div className='more' ref={menuRef}>
            <button
              type='button'
              className='action'
              aria-label='Applicant actions'
              aria-expanded={isMenuOpen}
              onClick={onMenuToggle}
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 20 20'
                fill='currentColor'
                stroke='currentColor'
                strokeWidth='0'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <circle cx='5' cy='10' r='1.5'></circle>
                <circle cx='10' cy='10' r='1.5'></circle>
                <circle cx='15' cy='10' r='1.5'></circle>
              </svg>
            </button>

            {isMenuOpen && (
              <div className='menu' role='menu'>
                {canResendInvite && (
                    <div>
                        <button
                          type='button'
                          className='resend'
                          role='menuitem'
                          disabled={isResending || isRemoving || resendRetrySeconds > 0}
                          onClick={onResendInvite}
                        >
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 20 20'
                            fill='none'
                            stroke='#111827'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          >
                            <path d='M15.5 10A5.5 5.5 0 115 6.5M14 4v3h-3'></path>
                          </svg>
                          {isResending ? 'Resending...' : resendRetrySeconds > 0 ? `Try again in ${resendRetrySeconds}s` : 'Resend invite'}
                        </button>
                        <div className='divider'></div>
                    </div>
                )}
                <button
                  type='button'
                  className='remove'
                  role='menuitem'
                  disabled={isResending || isRemoving}
                  onClick={onRemoveInvite}
                >
                  <svg
                    width='16'
                    height='16'
                    viewBox='0 0 20 20'
                    fill='none'
                    stroke='#EF4444'
                    strokeWidth='2.2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  >
                    <path d='M5 5l10 10M15 5L5 15'></path>
                  </svg>
                  {isRemoving ? 'Removing...' : removeLabel}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
